import type {
  InvestorDialogue,
  InvestorObjective,
  MeetingSummaryItem,
  MeetingsSummary,
  StaffDialogue,
  StaffObjective,
} from '@fruuit/shared'
import { Badge, Card, cn, EmptyState, StatusBadge, Timeline, type TimelineItem } from '@fruuit/ui'
import { CalendarDays, MessagesSquare } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { dateTimeIn, dateTimeUtc } from '../calendar/time'
import { MilestoneProgress, VisibilityTag } from './CaseRecord'
import { shortDate, stageName, themeLabel } from './format'
import { StageTrack } from './StageTrack'

/** Status, next action and the key dates (S5 engagement header). */
export function CaseSummary({
  status,
  nextAction,
  facts,
  extra,
}: {
  status: string
  nextAction: string | null
  facts: { label: string; value: ReactNode }[]
  extra?: ReactNode
}) {
  return (
    <section
      aria-label="Engagement summary"
      className="mb-6 grid gap-5 rounded-lg border border-line bg-wash p-5 sm:p-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,2fr)]"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={status} />
          {extra}
        </div>
        <p className="mt-3 text-sm leading-relaxed text-ink">
          <span className="font-semibold">Next action</span>{' '}
          {nextAction ?? 'No next action has been released.'}
        </p>
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        {facts.map((fact) => (
          <div key={fact.label} className="min-w-0">
            <dt className="text-xs font-medium text-sage">{fact.label}</dt>
            <dd className="mt-0.5 text-sm font-semibold text-ink">{fact.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

type AnyObjective = InvestorObjective | StaffObjective

function isStaffObjective(o: AnyObjective): o is StaffObjective {
  return 'state' in o
}

/**
 * One objective (S5): change requested, the stage track, what the evidence shows, what
 * remains unproven, and the next test. Staff also see every milestone with its state.
 */
export function ObjectiveCard({
  objective,
  caseTheme,
  actions,
}: {
  objective: AnyObjective
  caseTheme: string | null
  actions?: ReactNode
}) {
  const staff = isStaffObjective(objective)
  const published = objective.milestones.filter((m) => !('state' in m) || m.state === 'published')
  const latest = published[0] ?? null
  const changeRequest = objective.change_request
  const baseline = objective.baseline
  const owner = staff ? objective.owner.name : objective.owner_name

  return (
    <article className="border-b border-line-soft py-6 first:pt-0 last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[var(--tracking-kicker)] text-teal-500">
            {themeLabel(objective.category ?? caseTheme)}
          </p>
          <h3 className="mt-1 text-base font-semibold leading-snug text-ink">{objective.title}</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {staff ? <VisibilityTag released={objective.released !== null} /> : null}
          <StatusBadge status={objective.stage} label={stageName(objective.stage)} />
        </div>
      </div>
      {changeRequest ? (
        <p className="mt-2 text-sm leading-relaxed text-ink-soft">{changeRequest}</p>
      ) : null}
      <StageTrack stage={objective.stage} />
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div className="rounded-md bg-wash p-4">
          <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-teal-800">
            What the evidence shows
          </h4>
          <p className="mt-1.5 text-sm leading-relaxed text-ink">
            {latest?.rationale ?? baseline ?? 'No baseline has been released.'}
          </p>
        </div>
        <div className="rounded-md bg-cream p-4 ring-1 ring-line-soft">
          <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-teal-800">
            What remains unproven
          </h4>
          <p className="mt-1.5 text-sm leading-relaxed text-ink">
            {latest?.limitations ??
              'No independently reviewed milestone evidence has been released.'}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-3 text-sm">
        <p className="text-ink">
          <span className="font-semibold">Next test</span>{' '}
          {objective.next_action ?? 'Not yet released'}
          <span className="block text-xs text-sage">
            Owner {owner} · Due {shortDate(objective.due_date)}
          </span>
        </p>
        {actions}
      </div>
      <MilestoneProgress objective={objective} />
    </article>
  )
}

type AnyDialogue = InvestorDialogue | StaffDialogue

/**
 * The engagement record (S5): correspondence and meetings, newest first. Investors see
 * published correspondence only; staff see every state with a badge (GAP-UI-012).
 */
export function EngagementRecord({
  dialogues,
  meetings,
  meetingPath,
  action,
}: {
  dialogues: AnyDialogue[]
  meetings: MeetingsSummary
  meetingPath: (id: string) => string
  action?: ReactNode
}) {
  const items: (TimelineItem & { sort: string })[] = [
    ...dialogues.map((d) => ({
      id: d.id,
      sort: d.event_date,
      date: shortDate(d.event_date),
      dateNote: d.event_type_label,
      status: 'state' in d ? d.state : 'published',
      title: (
        <span className="inline-flex flex-wrap items-center gap-2">
          {d.title}
          {'state' in d && d.state !== 'published' ? <StatusBadge status={d.state} /> : null}
          {'state' in d ? <VisibilityTag released={d.state === 'published'} /> : null}
          {d.substantive ? <Badge tone="outline">Substantive</Badge> : null}
        </span>
      ),
      body: (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <p className="text-xs font-semibold text-teal-800">Company response</p>
            <p className="mt-1">{d.company_response}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-teal-800">Fruuit assessment</p>
            <p className="mt-1">{d.fruuit_judgment}</p>
          </div>
        </div>
      ),
      meta: `Next step: ${d.next_action} · ${shortDate(d.due_date)}`,
    })),
    ...meetings.items.map((m: MeetingSummaryItem) => ({
      id: m.id,
      sort: m.starts_at.slice(0, 10),
      date: shortDate(m.starts_at),
      dateNote: m.meeting_type,
      status: m.status === 'published' ? 'agenda published' : m.status,
      title: (
        <span className="inline-flex flex-wrap items-center gap-2">
          <Link to={meetingPath(m.id)} className="text-teal underline-offset-4 hover:underline">
            {m.title}
          </Link>
          {m.status !== 'published' ? <StatusBadge status={m.status} /> : null}
        </span>
      ),
      body: m.agenda,
      meta: m.has_published_minutes
        ? 'Reviewed meeting minutes available'
        : m.status === 'cancelled'
          ? 'This meeting was cancelled.'
          : `${dateTimeIn(m.starts_at, m.timezone)} · ${m.location}`,
    })),
  ].sort((a, b) => (a.sort < b.sort ? 1 : a.sort > b.sort ? -1 : 0))

  return (
    <Card eyebrow="Correspondence & meetings" title="The engagement record" actions={action}>
      {items.length === 0 ? (
        <EmptyState
          variant="plain"
          icon={<MessagesSquare />}
          title="No correspondence has been released yet."
          className="py-8"
        />
      ) : (
        <Timeline items={items} label="Engagement record" />
      )}
    </Card>
  )
}

/** A meeting's status for the compact list: cancelled, draft, minutes released, or nothing. */
function meetingNote(m: MeetingSummaryItem): string | null {
  if (m.status === 'cancelled') return 'Cancelled'
  if (m.status === 'draft') return 'Team draft'
  if (m.has_published_minutes) return 'Minutes released'
  return null
}

function MeetingList({
  items,
  meetingPath,
  label,
}: {
  items: MeetingSummaryItem[]
  meetingPath: (id: string) => string
  label: string
}) {
  return (
    <ul className="mt-1 divide-y divide-line-soft" aria-label={label}>
      {items.map((m) => {
        const note = meetingNote(m)
        return (
          <li key={m.id} className="flex items-start justify-between gap-3 py-2 text-sm">
            <span className="min-w-0">
              <Link
                to={meetingPath(m.id)}
                className={cn(
                  'block font-medium text-ink underline-offset-4 hover:text-teal hover:underline',
                  m.status === 'cancelled' && 'line-through text-sage',
                )}
              >
                {m.title}
              </Link>
              <span className="block text-xs text-sage">
                {m.meeting_type}
                {note ? ` · ${note}` : ''}
              </span>
            </span>
            <span
              className="shrink-0 text-right text-xs text-sage"
              title={dateTimeUtc(m.starts_at)}
            >
              {dateTimeIn(m.starts_at, m.timezone)}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

const PAST_LIMIT = 6

/**
 * The case's meetings in one place (demo step 3): the next published meeting first, then the
 * other upcoming ones and the meetings already held, each linking to its page. Staff also see
 * team drafts here; investors only ever get published and cancelled meetings.
 */
export function MeetingsCard({
  meetings,
  meetingPath,
  now = new Date().toISOString(),
}: {
  meetings: MeetingsSummary
  meetingPath: (id: string) => string
  /** The clock to split past from upcoming (tests pin it). */
  now?: string
}) {
  const next = meetings.next
  const upcoming = meetings.items
    .filter((m) => m.starts_at > now && m.id !== next?.id)
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at))
  const past = meetings.items
    .filter((m) => m.starts_at <= now)
    .sort((a, b) => b.starts_at.localeCompare(a.starts_at))
  return (
    <Card
      title="Meetings"
      padding="md"
      actions={
        <span className="text-xs text-sage">
          {meetings.upcoming_count} upcoming · {meetings.past_count} held
        </span>
      }
    >
      {next ? (
        <div>
          <p className="text-xs font-semibold uppercase tracking-[var(--tracking-kicker)] text-teal-500">
            Next · {next.meeting_type}
          </p>
          <Link
            to={meetingPath(next.id)}
            className="mt-1 block text-[15px] font-semibold text-ink underline-offset-4 hover:text-teal hover:underline"
          >
            {next.title}
          </Link>
          <p
            className="mt-1 flex items-center gap-1.5 text-sm text-ink-soft"
            title={dateTimeUtc(next.starts_at)}
          >
            <CalendarDays aria-hidden="true" className="size-4 text-teal-500" />
            {dateTimeIn(next.starts_at, next.timezone)}
          </p>
          <p className="mt-0.5 text-xs text-sage">
            {next.location} · Participation deadline{' '}
            {dateTimeIn(next.participation_cutoff, next.timezone)}
          </p>
        </div>
      ) : (
        <p className="text-sm text-sage">No upcoming meeting is scheduled for this case.</p>
      )}
      {upcoming.length > 0 ? (
        <div className="mt-4 border-t border-line pt-3">
          <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-sage">
            Also upcoming
          </h3>
          <MeetingList items={upcoming} meetingPath={meetingPath} label="Other upcoming meetings" />
        </div>
      ) : null}
      {past.length > 0 ? (
        <div className="mt-4 border-t border-line pt-3">
          <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-sage">
            Past meetings
          </h3>
          <MeetingList
            items={past.slice(0, PAST_LIMIT)}
            meetingPath={meetingPath}
            label="Past meetings"
          />
          {past.length > PAST_LIMIT ? (
            <p className="mt-1 text-xs text-sage">
              And {past.length - PAST_LIMIT} more in the engagement record.
            </p>
          ) : null}
        </div>
      ) : null}
    </Card>
  )
}
